<template>
    <div>
        <DataTable
            :perPage="itemsPerPage"
            :columns="tableColumns"
            :items="paginatedItems"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="true"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @drag-end="handleDragEnd"
            @sort="handleSort"
            @per-page-changed="handlePerPageChange"
            :sortBy="sortBy"
            :sortOrder="sortOrder"
        >
            <!-- Toolbar -->
            <template #toolbar>
                <div class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-gap-2 ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :options="[
                                { label: 'CSV', value: 'csv' },
                                { label: 'Excel', value: 'excel' },
                            ]"
                            :disabled="isExporting"
                            @export-selected="exportData"
                        />

                        <!-- Filter Button -->
                        <button
                            @click="openFilterModal"
                            class="filterButton ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        >
                            <LayoutGrid :size="17" />
                            <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">
                                {{ $t('common.filter') }}
                            </span>
                            <span
                                v-if="activeFiltersCount > 0"
                                class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-bg-[#7D2DBD] ppv2-text-white ppv2-text-[10px] ppv2-w-5 ppv2-h-5 ppv2-rounded-full poppinsSemiBold"
                            >
                                {{ activeFiltersCount }}
                            </span>
                        </button>

                        <!-- Fin des commandes (design: Fin des commandes : X min avant) -->
                        <FilterByType
                            variant="cutoff"
                            icon="Clock"
                            :label="$t('food.tables.order_cutoff.label')"
                            :dropdown-title="$t('food.tables.order_cutoff.block_title')"
                            :dropdown-footer="cutoffFooter"
                            :options="orderCutoffOptions"
                            :selected="orderCutoff"
                            @filter-selected="selectOrderCutoff"
                        />

                        <AdvancedFilterModal
                            v-model="showFilterModal"
                            :initialFilters="advancedFilters"
                            :availableFilters="['rooms']"
                            :rooms="filterData.rooms"
                            @apply="handleAdvancedFilters"
                        />
                    </div>

                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="openAddModal"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('common.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(14) }">{{ item.name }}</span>
            </template>

            <template #column-category_name="{ item }">
                <BaseSelect
                    :value="item.category ? item.category.id : null"
                    :options="categoryOptions"
                    optionLabel="name"
                    optionValue="id"
                    :clearable="true"
                    placeholder="Aucune"
                    @input="updateTableCategory(item, $event)"
                />
            </template>

            <!-- Column: Order Link -->
            <template #column-order_link="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-gap-2 lienToCopy ppv2-text-black">
                    <a
                        :href="item.order_link"
                        target="_blank"
                        class="ppv2-truncate ppv2-max-w-[80%] ppv2-underline ppv2-text-black"
                    >
                        {{ item.order_link }}
                    </a>
                    <div class="ppv2-relative">
                        <button @click="copyLink(item.order_link, item.id)" :title="$t('common.copy_link')">
                            <ClipboardCopy v-if="copiedId !== item.id" :size="16" class="showOnHover" />
                            <ClipboardCheck color="#7D2DBD" v-else :size="16" />
                        </button>
                        <!-- Copy Indicator Badge -->
                        <transition name="fade">
                            <div v-if="copiedId === item.id" class="copyBadge poppinsRegular">{{ $t('common.copied') }}</div>
                        </transition>
                    </div>
                </div>
            </template>

            <!-- Column: Menu -->
            <template #column-menu_name="{ item }">
                <span class="ppv2-underline ppv2-text-black">{{ item.menu_name }}</span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3">
                    <button
                        @click="openEditModal(item)"
                        class="ppv2-p-1 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        @click="duplicateItem(item)"
                        class="ppv2-p-1 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded"
                    >
                        <CopyPlus :size="18" />
                    </button>
                    <button
                        @click="downloadQrCode(item)"
                        class="ppv2-p-1 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded"
                        :title="$t('food.tables.download_qr')"
                    >
                        <Download :size="18" />
                    </button>
                    <button
                        @click="deleteSingleItem(item.id)"
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions -->
            <template #bulk-actions="{ selectedCount }">
                <span class="poppinsRegular ppv2-text-black">{{
                    $t('food.tables.selected_items', { count: selectedCount })
                }}</span>
                <!-- <BaseButton variant="outline" customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2">
                    <LayoutGrid :size="18" /><span :style="{ fontSize: normalizeFontSize(14) }">Modifier catégorie</span>
                </BaseButton> -->
                <BaseButton
                    variant="outline"
                    @click="downloadMultiplePdf"
                    :loading="isDownloadingMultiple"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Download :size="18" /><span :style="{ fontSize: normalizeFontSize(14) }">{{
                        $t('food.tables.download_all_qr')
                    }}</span>
                </BaseButton>
                <BaseButton
                    variant="dangerOutline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="18" /><span :style="{ fontSize: normalizeFontSize(14) }">{{ $t('common.delete') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <CustomDialog v-model="showDeletePopup" :defaultTitle="$t('common.delete')" noScroll>
            <template #content>
                <p class="ppv2-text-center">{{ deletePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton variant="outline" @click="showDeletePopup = false">{{ $t('common.cancel') }}</BaseButton>
                <BaseButton variant="danger" @click="confirmDelete">{{ $t('common.delete') }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Duplicate Confirmation Popup -->
        <CustomDialog v-model="showDuplicatePopup" :defaultTitle="$t('common.duplicate')" height="250px" minHeight="250px">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular">{{ duplicatePopupDescription }}</p>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDuplicatePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                    >{{ $t('common.cancel') }}</BaseButton
                >
                <BaseButton variant="primary" @click="confirmDuplicate" :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`">
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Add QR Code Modal -->
        <CustomDialog
            v-model="showAddModal"
            :defaultTitle="$t('food.tables.add_modal_title')"
            :minHeight="isAddSelectOpen ? '62vh' : ''"
        >
            <template #content>
                <!-- Info Message -->
                <div
                    class="ppv2-flex ppv2-items-start ppv2-gap-3 ppv2-px-2 ppv2-py-3 ppv2-bg-[#2C3FE8] ppv2-bg-opacity-10 ppv2-rounded-[10px] ppv2-mb-6 ppv2-border ppv2-border-[#2C3FE8]"
                >
                    <Info :size="20" class="ppv2-text-[#2C3FE8] ppv2-flex-shrink-0 ppv2-mt-0.5" />
                    <div class="ppv2-text-sm ppv2-text-black poppinsRegular">
                        <p class="ppv2-mb-0">{{ $t('food.tables.info_message') }}</p>
                    </div>
                </div>

                <div class="ppv2-flex md:ppv2-flex-row ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Number of QR Codes -->
                    <div class="ppv2-flex-1">
                        <CustomInput
                            v-model="newQrCount"
                            :label="$t('food.tables.count_label')"
                            type="number"
                            required
                            :min="1"
                            :placeholder="$t('food.tables.count_placeholder')"
                        />
                    </div>
                    <!-- Target Category -->
                    <div class="ppv2-flex-1">
                        <BaseSelect
                            v-model="selectedCategoryIds"
                            :label="$t('food.tables.categories_label')"
                            :placeholder="$t('food.tables.categories_placeholder')"
                            :options="categories"
                            optionLabel="name"
                            optionValue="id"
                            :multiple="true"
                            :clearable="true"
                            @open="isAddSelectOpen = true"
                            @close="isAddSelectOpen = false"
                            fixed-dropdown
                        />
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="primary" @click="saveQrCodes" :disabled="!newQrCount || newQrCount < 1">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>

        <!-- Edit QR Code Modal -->
        <CustomDialog
            v-model="showEditModal"
            :defaultTitle="$t('food.tables.edit_modal_title')"
            :minHeight="isEditSelectOpen ? '62vh' : ''"
        >
            <template #content>
                <div class="ppv2-grid ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Name -->
                    <CustomInput
                        v-model="editForm.name"
                        :label="$t('food.tables.name_label')"
                        :placeholder="$t('food.tables.name_placeholder')"
                        :required="true"
                    />

                    <!-- Target Category -->
                    <BaseSelect
                        v-model="editForm.category_ids"
                        :label="$t('food.tables.categories_label')"
                        :placeholder="$t('food.tables.categories_placeholder')"
                        :options="categories"
                        optionLabel="name"
                        optionValue="id"
                        :multiple="true"
                        :clearable="true"
                        @open="isEditSelectOpen = true"
                        @close="isEditSelectOpen = false"
                        fixed-dropdown
                    />
                </div>
            </template>
            <template #actions>
                <BaseButton variant="primary" @click="updateQrCode" :disabled="!editForm.name">{{
                    $t('common.confirm')
                }}</BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import {
    LayoutGrid,
    ChevronDown,
    Plus,
    SquarePen,
    CopyPlus,
    Trash2,
    ClipboardCopy,
    ClipboardCheck,
    Hash,
    Percent,
    Info,
    Download,
} from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import Search from '../Experience/components/Search.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import AdvancedFilterModal from '../../Shared/AdvancedFilterModal.vue'
import FilterByType from '../../Shared/FilterByType.vue'
import axios from 'axios'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'BoFoodV3Tables',
    components: {
        LayoutGrid,
        ChevronDown,
        Plus,
        SquarePen,
        CopyPlus,
        Trash2,
        ClipboardCopy,
        ClipboardCheck,
        Hash,
        Percent,
        Info,
        Download,
        Search,
        BaseButton,
        CustomInput,
        BaseSelect,
        CustomDialog,
        DataTable,
        ExportSelect,
        AdvancedFilterModal,
        FilterByType,
    },
    props: {
        establishment: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('food_tables_items_per_page', 10),
            selectedItems: [],
            sortBy: 'order',
            sortOrder: 'asc',
            showFilterModal: false,
            // Fin des commandes via FilterByType — selection persisted in website_settings
            orderCutoff: null,
            previousCutoff: null,
            isSavingCutoff: false,
            todayClose: null,
            todayLastOrder: null,
            isOpenToday: false,
            // Delete Popup
            showDeletePopup: false,
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            // Duplicate Popup
            showDuplicatePopup: false,
            duplicatePopupDescription: '',
            itemToDuplicate: null,

            items: [],
            totalPages: 1,
            isExporting: false,
            isDownloadingMultiple: false,
            isSavingPositions: false,
            copiedId: null,
            advancedFilters: {
                room_ids: [],
            },
            filterData: {
                rooms: [],
            },
            // Add QR Code Modal
            showAddModal: false,
            newQrCount: 1,
            selectedCategoryIds: [],
            categories: [],
            tableCategories: [],
            // Edit QR Code Modal
            showEditModal: false,
            editForm: {
                id: null,
                name: '',
                category_ids: [],
            },
            isAddSelectOpen: false,
            isEditSelectOpen: false,
        }
    },
    computed: {
        tableColumns() {
            return [
                { key: 'name', label: this.$t('food.tables.columns.name'), sortable: true, type: 'text' },
                { key: 'category_name', label: 'Catégorie', sortable: false, type: 'text' },
                { key: 'order_link', label: this.$t('food.tables.columns.order_link'), sortable: false, type: 'text' },
                // { key: 'menu_name', label: 'Carte / Menu', sortable: true, headerClass: 'ppv2-text-left' },
                { key: 'actions', label: this.$t('food.tables.columns.actions'), sortable: false, type: 'actions' },
            ]
        },
        paginatedItems() {
            return this.items
        },
        activeFiltersCount() {
            let count = 0
            if (this.advancedFilters.room_ids?.length > 0) count++
            return count
        },
        categoryOptions() {
            return [{ id: null, name: 'Aucune' }, ...this.tableCategories]
        },
        cutoffFooter() {
            if (!this.isOpenToday) return this.$t('food.tables.order_cutoff.closed_today')
            const time = this.todayLastOrder || this.todayClose
            if (!time) return ''
            return this.$t('food.tables.order_cutoff.today_last_order', { time })
        },
        orderCutoffOptions() {
            return [
                { value: null, label: this.$t('food.tables.order_cutoff.disabled') },
                {
                    value: 10,
                    label: this.$t('food.tables.order_cutoff.min_before_close', { count: 10 }),
                    shortLabel: this.$t('food.tables.order_cutoff.short_min_before', { count: 10 }),
                },
                {
                    value: 15,
                    label: this.$t('food.tables.order_cutoff.min_before_close', { count: 15 }),
                    shortLabel: this.$t('food.tables.order_cutoff.short_min_before', { count: 15 }),
                },
                {
                    value: 20,
                    label: this.$t('food.tables.order_cutoff.min_before_close', { count: 20 }),
                    shortLabel: this.$t('food.tables.order_cutoff.short_min_before', { count: 20 }),
                },
                {
                    value: 30,
                    label: this.$t('food.tables.order_cutoff.min_before_close', { count: 30 }),
                    shortLabel: this.$t('food.tables.order_cutoff.short_min_before', { count: 30 }),
                },
                {
                    value: 60,
                    label: this.$t('food.tables.order_cutoff.one_hour_before_close'),
                    shortLabel: this.$t('food.tables.order_cutoff.short_one_hour'),
                },
            ]
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        handlePerPageChange(val) {
            this.itemsPerPage = Number(val)
            setPerPage('food_tables_items_per_page', this.itemsPerPage)
            this.currentPage = 1
            this.fetchData()
        },
        handleSearchUpdate(val) {
            this.searchQuery = val
            this.currentPage = 1
            this.fetchData()
        },
        toggleItemSelection(id) {
            const idx = this.selectedItems.indexOf(id)
            idx > -1 ? this.selectedItems.splice(idx, 1) : this.selectedItems.push(id)
        },
        toggleSelectAll() {
            this.selectedItems = this.selectedItems.length === this.items.length ? [] : this.items.map((i) => i.id)
        },
        copyLink(link, id) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(link)
                    .then(() => {
                        this.handleCopySuccess(id)
                    })
                    .catch((err) => {
                        console.error('Copy failed:', err)
                        this.fallbackCopy(link, id)
                    })
            } else {
                this.fallbackCopy(link, id)
            }
        },
        fallbackCopy(text, id) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            // Ensure it's not visible but part of the DOM
            textArea.style.position = 'fixed'
            textArea.style.left = '-9999px'
            textArea.style.top = '0'
            document.body.appendChild(textArea)

            textArea.focus()
            textArea.select()

            try {
                const successful = document.execCommand('copy')
                if (successful) {
                    this.handleCopySuccess(id)
                } else {
                    console.error('Fallback copy failed')
                }
            } catch (err) {
                console.error('Fallback copy error', err)
            }

            document.body.removeChild(textArea)
        },
        handleCopySuccess(id) {
            this.copiedId = id
            setTimeout(() => {
                if (this.copiedId === id) this.copiedId = null
            }, 2000)
        },
        handleSort(key) {
            if (this.sortBy === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortBy = key
                this.sortOrder = 'asc'
            }
            this.fetchData()
        },
        async downloadQrCode(item) {
            try {
                const res = await axios.get('/back-office/v3/food/tables/download-qr-code', {
                    params: {
                        qr_code_url: item.qr_code_url,
                        table_id: item.id,
                    },
                    responseType: 'blob',
                })
                this.forceFileDownload(res, `qr_table_${item.id}.png`)
            } catch (e) {
                console.error(e)
                this.$toast?.error(this.$t('food.tables.errors.download'))
            }
        },
        async downloadMultiplePdf() {
            if (this.isDownloadingMultiple || !this.selectedItems.length) return

            console.log('Starting multiple PDF download...')
            this.isDownloadingMultiple = true
            try {
                const res = await axios.post(
                    '/back-office/v3/food/tables/download-multiple-pdf',
                    {
                        ids: this.selectedItems,
                        per_row: 3,
                    },
                    { responseType: 'blob' }
                )
                this.forceFileDownload(res, 'tables-qr-codes.pdf')
            } catch (e) {
                console.error(e)
                this.$toast?.error(this.$t('food.tables.errors.download'))
            } finally {
                this.isDownloadingMultiple = false
            }
        },
        forceFileDownload(response, defaultFilename) {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url

            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
            let fileName = defaultFilename
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename=(?:"([^"]+)"|([^;]+))/)
                if (fileNameMatch) {
                    fileName = fileNameMatch[1] || fileNameMatch[2]
                }
            }

            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        },
        async openFilterModal() {
            if (this.filterData.rooms.length === 0) {
                try {
                    const response = await axios.get('/back-office/v3/food/get-filters-data')
                    this.filterData = response.data.data
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                }
            }
            this.showFilterModal = true
        },
        handleAdvancedFilters(filters) {
            this.advancedFilters = filters
            this.currentPage = 1
            this.fetchData()
        },
        normalizeOrderCutoff(value) {
            if (value === null || value === undefined || value === '') return null
            const num = Number(value)
            return Number.isNaN(num) ? null : num
        },
        selectOrderCutoff(type) {
            this.saveOrderCutoff(type)
        },
        async fetchOrderCutoff() {
            try {
                const response = await axios.get('/back-office/v3/food/tables/order-cutoff')
                this.orderCutoff = this.normalizeOrderCutoff(response.data?.data?.value)
                this.previousCutoff = this.orderCutoff
                this.todayClose = response.data?.data?.today_close || null
                this.todayLastOrder = response.data?.data?.today_last_order || null
                this.isOpenToday = !!response.data?.data?.is_open_today
            } catch (e) {
                console.error(e)
            }
        },
        subtractMinutesFromTime(time, minutes) {
            if (!time || !minutes) return time
            const match = /^(\d{1,2}):(\d{2})/.exec(time)
            if (!match) return time
            let total = parseInt(match[1], 10) * 60 + parseInt(match[2], 10) - minutes
            total = ((total % 1440) + 1440) % 1440
            return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
        },
        async saveOrderCutoff(value) {
            if (this.isSavingCutoff) return
            this.isSavingCutoff = true
            this.orderCutoff = this.normalizeOrderCutoff(value)
            // Optimistic footer update from known closing time.
            if (this.todayClose) {
                this.todayLastOrder = this.orderCutoff
                    ? this.subtractMinutesFromTime(this.todayClose, this.orderCutoff)
                    : this.todayClose
            }
            try {
                const response = await axios.put('/back-office/v3/food/tables/order-cutoff', {
                    value: this.orderCutoff,
                })
                this.orderCutoff = this.normalizeOrderCutoff(response.data?.data?.value)
                this.previousCutoff = this.orderCutoff
                if (response.data?.data?.today_close) this.todayClose = response.data.data.today_close
                if (response.data?.data?.today_last_order !== undefined)
                    this.todayLastOrder = response.data.data.today_last_order || null
                if (response.data?.data?.is_open_today !== undefined) this.isOpenToday = !!response.data.data.is_open_today
                if (this.orderCutoff === null) {
                    this.$toast?.success(this.$t('food.tables.order_cutoff.disabled_success'))
                } else {
                    this.$toast?.success(this.$t('food.tables.order_cutoff.save_success', { count: this.orderCutoff }))
                }
            } catch (e) {
                console.error(e)
                this.orderCutoff = this.previousCutoff
                this.$toast?.error(this.$t('food.tables.order_cutoff.save_error'))
            } finally {
                this.isSavingCutoff = false
            }
        },
        deleteSingleItem(id) {
            this.deleteAction = 'single'
            this.itemToDelete = id
            this.deletePopupDescription = this.$t('food.tables.delete_single_confirm')
            this.showDeletePopup = true
        },
        deleteSelectedItems() {
            if (!this.selectedItems.length) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('food.tables.delete_multiple_confirm', { count: this.selectedItems.length })
            this.showDeletePopup = true
        },
        async confirmDelete() {
            const ids = this.deleteAction === 'single' ? [this.itemToDelete] : this.selectedItems
            try {
                await axios.delete('/back-office/v3/food/tables/delete', { data: { ids } })
                this.fetchData()
                this.selectedItems = []
                this.showDeletePopup = false
                this.$emit('refresh-stats')
            } catch {
                this.$toast?.error(this.$t('food.tables.errors.generic'))
            }
        },
        duplicateItem(item) {
            this.itemToDuplicate = item
            this.duplicatePopupDescription = this.$t('food.tables.duplicate_confirm')
            this.showDuplicatePopup = true
        },
        async confirmDuplicate() {
            if (!this.itemToDuplicate) return

            try {
                const response = await axios.post('/back-office/v3/food/tables/duplicate', { id: this.itemToDuplicate.id })
                if (response.data.success) {
                    this.$toast?.success(this.$t('food.tables.duplicate_success'))
                    this.fetchData()
                    this.$emit('refresh-stats')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error duplicating table:', error)
                this.$toast?.error(this.$t('food.tables.errors.duplicate'))
            } finally {
                this.showDuplicatePopup = false
                this.itemToDuplicate = null
            }
        },
        async openEditModal(item) {
            this.editForm = {
                id: item.id,
                name: item.name,
                category_ids: item.categories ? item.categories.map((c) => c.id) : [],
            }
            if (this.categories.length === 0) {
                await this.fetchCategories()
            }
            this.showEditModal = true
        },
        async updateQrCode() {
            try {
                await axios.post(`/back-office/v3/food/tables/update/${this.editForm.id}`, {
                    name: this.editForm.name,
                    category_ids: this.editForm.category_ids,
                })
                this.showEditModal = false
                this.$toast?.success(this.$t('food.tables.update_success'))
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                this.$toast?.error(this.$t('food.tables.errors.update'))
            }
        },
        async openAddModal() {
            this.newQrCount = 1
            this.selectedCategoryIds = []
            if (this.categories.length === 0) {
                await this.fetchCategories()
            }
            this.showAddModal = true
        },
        async fetchCategories() {
            try {
                const res = await axios.get('/back-office/v3/food/categories/fast-list')
                this.categories = res.data.data || []
            } catch (e) {
                console.error(e)
            }
        },
        async fetchTableCategories() {
            try {
                const res = await axios.get('/back-office/v3/food/table-categories/options')
                this.tableCategories = res.data.data || []
            } catch (e) {
                console.error(e)
            }
        },
        async updateTableCategory(item, categoryId) {
            const previous = item.category
            item.category = categoryId ? this.tableCategories.find((category) => category.id === categoryId) : null
            try {
                await axios.patch(`/back-office/v3/food/tables/update-category/${item.id}`, { category_id: categoryId })
            } catch (e) {
                item.category = previous
                this.$toast?.error('Impossible de modifier la catégorie')
            }
        },
        async saveQrCodes() {
            try {
                await axios.post('/back-office/v3/food/tables/store', {
                    number_of_tables: this.newQrCount,
                    category_ids: this.selectedCategoryIds,
                })
                this.showAddModal = false
                this.$toast?.success(this.$t('food.tables.create_success'))
                this.fetchData()
                this.$emit('refresh-stats')
            } catch (e) {
                console.error(e)
                this.$toast?.error(this.$t('food.tables.errors.create'))
            }
        },
        async fetchData() {
            try {
                const params = {
                    search: this.searchQuery,
                    currentPage: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                    ...this.advancedFilters,
                }
                const response = await axios.get('/back-office/v3/food/tables/list', { params })
                this.items = response.data.data || []
                this.totalPages = response.data.last_page || 1
            } catch (e) {
                console.error(e)
            }
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            // Move the item locally
            const movedItem = this.items.splice(oldIndex, 1)[0]
            this.items.splice(newIndex, 0, movedItem)

            // Calculate start order index based on pagination
            const startOrder = (this.currentPage - 1) * this.itemsPerPage
            const changedPositions = []

            // Re-assign order based on new index
            this.items.forEach((item, index) => {
                const newOrder = startOrder + index

                // If order changed, add to update list
                changedPositions.push({
                    id: item.id,
                    order: newOrder,
                })
                item.order = newOrder
            })

            // Save if there are changes
            if (changedPositions.length > 0) {
                await this.savePositions(changedPositions)
            }
        },
        async savePositions(positions) {
            if (this.isSavingPositions) return
            this.isSavingPositions = true

            try {
                await axios.put('/back-office/v3/food/tables/update-positions', {
                    positions,
                })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving positions:', error)
                this.fetchData()
            } finally {
                this.isSavingPositions = false
            }
        },
        async exportData(format = 'csv') {
            if (this.isExporting) return

            this.isExporting = true
            try {
                const res = await axios.get('/back-office/v3/food/tables/export', {
                    params: { format },
                    responseType: 'blob',
                })
                const url = window.URL.createObjectURL(new Blob([res.data]))
                const link = document.createElement('a')
                const fileName = `tables_export_${format}.${format === 'excel' ? 'xlsx' : 'csv'}`
                link.href = url
                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                console.error(e)
                this.$toast?.error(this.$t('food.tables.errors.export'))
            } finally {
                this.isExporting = false
            }
        },
    },
    mounted() {
        this.fetchTableCategories()
        this.fetchOrderCutoff()
        this.fetchData()
    },
}
</script>

<style scoped lang="scss">
.copyBadge {
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 8px;
    background-color: #1a1f37;
    color: white;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10;

    &::before {
        content: '';
        position: absolute;
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        border: 4px solid transparent;
        border-right-color: #1a1f37;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.showOnHover {
    opacity: 0;
}

.lienToCopy:hover .showOnHover {
    opacity: 1;
}
</style>
